/**
 * MailComposeModal — Antwort verfassen (Plan 5.1-04 / D-06).
 *
 *  - To = lead.vereinEmail (editierbar — Sonderfaelle moeglich)
 *  - From = teamshops@anstoss24.de (read-only, kein From-Spoofing erlaubt)
 *  - Subject = "Re: <originaler-Subject>" (editierbar)
 *  - Body = Plaintext-Textarea (kein Rich-Text in v1)
 *  - Senden ruft Backend POST /leads/:id/reply
 *  - Bei Allowlist-Miss im Dry-Run: Tool zeigt simulated-Hinweis
 */

import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@libero/core/components/ui/button';
import { leadsApi } from '../services/leadsApi';
import type { Lead } from '../types';

interface Props {
  lead: Lead;
  /** Subject der letzten inbound Mail — wird zu "Re: ..." */
  replyToSubject: string;
  /** Message-ID der letzten inbound Mail — wird als In-Reply-To Header gesetzt */
  replyToMessageId: string | null;
  onClose: () => void;
  onSent: () => void;
}

export default function MailComposeModal({
  lead,
  replyToSubject,
  onClose,
  onSent,
}: Props) {
  const [to, setTo] = useState(lead.vereinEmail ?? '');
  const [subject, setSubject] = useState(
    replyToSubject ? `Re: ${replyToSubject.replace(/^Re:\s*/i, '')}` : '',
  );
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [simulatedInfo, setSimulatedInfo] = useState<string | null>(null);

  function isValidEmail(s: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  async function handleSend() {
    if (!isValidEmail(to)) {
      setError('Bitte gueltige Empfaenger-Mail eingeben');
      return;
    }
    if (!subject.trim()) {
      setError('Betreff darf nicht leer sein');
      return;
    }
    if (!body.trim()) {
      setError('Nachricht darf nicht leer sein');
      return;
    }
    setSending(true);
    setError(null);
    setSimulatedInfo(null);
    try {
      const res = await leadsApi.reply(lead.id, {
        to: to.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });
      if (res?.simulated) {
        setSimulatedInfo(res.message ?? 'Test-Modus: Mail nur in DB gespeichert, kein Versand');
        // Im simulated-Fall trotzdem nach kurzer Anzeige schliessen
        setTimeout(() => onSent(), 1800);
      } else {
        onSent();
      }
    } catch (e: any) {
      setError(e.response?.data?.error?.message ?? 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1A1A1A]">Antwort verfassen</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[#DAD9D9]/40 rounded"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
        {simulatedInfo && (
          <div className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded p-2">
            {simulatedInfo}
          </div>
        )}

        <div>
          <label className="text-sm font-medium block mb-1">An <span className="text-red-500">*</span></label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="empfaenger@verein.de"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Vorbelegt mit der Vereins-Adresse aus dem Plenty-Formular. Bei Sonderfaellen aenderbar.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Von</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm bg-[#DAD9D9]/30 text-muted-foreground"
            value="teamshops@anstoss24.de"
            readOnly
            disabled
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Betreff <span className="text-red-500">*</span></label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Re: ..."
            maxLength={200}
          />
        </div>

        <div>
          <label className="text-sm font-medium block mb-1">Nachricht <span className="text-red-500">*</span></label>
          <textarea
            className="w-full border rounded px-3 py-2 text-sm font-sans resize-none"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hallo, ..."
            rows={10}
            maxLength={10000}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Plain-Text. {body.length} / 10000 Zeichen.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={sending}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSend()}
            disabled={sending || !to || !subject || !body}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {sending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            {sending ? 'Sendet...' : 'Senden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
